import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/useAuthStore';
import { useCompanyStore } from '../stores/useCompanyStore';
import { logger } from '../utils/logger';

const CompanySwitcherDropdown: React.FC = () => {
  const { usuario } = useAuthStore();
  const { empresas, currentEmpresa, switchEmpresa, fetchUserEmpresas } = useCompanyStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  // Fetch user empresas on component mount
  useEffect(() => {
    if (usuario?.id && empresas.length === 0) {
      fetchUserEmpresas(usuario.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.id, empresas.length]); // Remove fetchUserEmpresas from dependencies to prevent infinite loops

  // Only log when empresas change, not every render
  const prevEmpresasLength = useRef(empresas?.length);
  const prevCurrentEmpresaId = useRef(currentEmpresa?.id);
  
  useEffect(() => {
    if (
      currentEmpresa?.id !== prevCurrentEmpresaId.current ||
      empresas?.length !== prevEmpresasLength.current
    ) {
      logger.debug('🏢 Companies:', { 
        current: currentEmpresa?.nombre, 
        count: empresas?.length 
      });
      prevCurrentEmpresaId.current = currentEmpresa?.id;
      prevEmpresasLength.current = empresas?.length;
    }
  }, [currentEmpresa?.id, currentEmpresa?.nombre, empresas?.length]);

  if (!currentEmpresa || !empresas || empresas.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className={`flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          isSwitching 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:bg-gray-50'
        }`}
      >
        {currentEmpresa.logo ? (
          <img 
            src={currentEmpresa.logo} 
            alt={currentEmpresa.nombre}
            className="w-6 h-6 rounded-full object-cover"
          />
        ) : (
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
            {currentEmpresa.nombre.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="font-medium text-sm">{currentEmpresa.nombre}</span>
        {isSwitching ? (
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        ) : (
          <svg 
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {isSwitching ? '🔄 Cambiando de empresas...' : 'Cambiar Empresa'}
            </div>
            
            <div className="space-y-1">
              {empresas.map((empresa) => (
                <button
                  key={empresa.id}
                  onClick={async () => {
                    if (isSwitching || currentEmpresa.id === empresa.id) return;
                    setIsSwitching(true);
                    setIsOpen(false);
                    
                    try {
                      await switchEmpresa(empresa.id);
                      
                      // Reload page to refresh all data
                      window.location.reload();
                    } catch (error) {
                      logger.error('Error switching company:', error);
                      setIsSwitching(false);
                      alert('Error al cambiar de empresa. Por favor, intenta de nuevo.');
                    }
                  }}
                  disabled={isSwitching || currentEmpresa.id === empresa.id}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    currentEmpresa.id === empresa.id 
                      ? 'bg-blue-50 border border-blue-200 cursor-not-allowed opacity-75' 
                      : isSwitching
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {empresa.nombre.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {empresa.nombre}
                    </div>
                    {empresa.descripcion && (
                      <div className="text-xs text-gray-500 truncate">
                        {empresa.descripcion}
                      </div>
                    )}
                  </div>
                  {currentEmpresa.id === empresa.id && (
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CompanySwitcherDropdown;
